import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import PropTypes from 'prop-types';
import logo from '../../assets/logo.png';

const styles = StyleSheet.create({
  page: {
    padding: 30,
  },
  logo: {
    width: 100,
    marginBottom: 20,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  header: {
    fontSize: 12,
    textAlign: 'right',
    marginBottom: 20,
  },
  title: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
  },
  section: {
    marginBottom: 10,
  },
  bold: {
    fontWeight: 'bold',
  },
  text: {
    fontSize: 12,
    marginBottom: 10,
  },
  link: {
    color: 'blue',
    textDecoration: 'underline',
  },
  table: {
    display: 'table',
    width: 'auto',
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableCol: {
    width: '50%',
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  tableCell: {
    margin: 5,
    fontSize: 10,
  },
  checkBox: {
    width: 12,
    height: 12,
    border: '1px solid black',
    marginRight: 5,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  checkBoxRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkMark: {
    fontSize: 10,
    position: 'absolute',
    top: 0,
    left: 2,
    color: 'black',
    fontWeight: 'bold',
  },
  checkBoxGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  checkBoxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 15,
  },
  fullWidthCol: {
    width: '100%',
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  vehicleDetailsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  vehicleDetailCol: {
    width: '25%',
    paddingHorizontal: 4,
  },
});

const CheckBox = ({ checked }) => (
  <View style={styles.checkBox}>
    {checked && <Text style={styles.checkMark}>X</Text>}
  </View>
);

CheckBox.propTypes = {
  checked: PropTypes.bool.isRequired,
};

const DonationRequestWorksheet = ({ donation }) => {
  // Check if pickup location is same as donor address
  const isPickupAddressEmpty = 
    !donation.pickup.pickupAddress &&
    !donation.pickup.pickupCity &&
    !donation.pickup.pickupState &&
    !donation.pickup.pickupZip;

  const isAddressMatch = 
    donation.pickup.address === donation.pickup.pickupAddress &&
    donation.pickup.city === donation.pickup.pickupCity &&
    donation.pickup.state === donation.pickup.pickupState &&
    donation.pickup.zip === donation.pickup.pickupZip;

  const isSameAddress = isPickupAddressEmpty || isAddressMatch;

  return (
    <Document>
      <Page style={styles.page}>
        <Image src={logo} style={styles.logo}/>
        <View style={styles.title}>
          <Text>Vehicles For Young Veterans</Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.bold}>Donor Information</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={styles.fullWidthCol}>
                <Text style={styles.tableCell}>Donor Name: {donation.contact.title} {donation.contact.firstName} {donation.contact.lastName}</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Day Phone: {donation.contact.phone}</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Alt Phone: {donation.contact.altPhone || 'N/A'}</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Address: {donation.pickup.address}</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>City: {donation.pickup.city}</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>State: {donation.pickup.state}, Zip: {donation.pickup.zip}</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Email: {donation.contact.email}</Text>
              </View>
            </View>
          </View>
        </View>
        <View style={styles.section}>
          <Text style={styles.bold}>Vehicle Information</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={styles.fullWidthCol}>
                <Text style={styles.tableCell}>Location: {isSameAddress ? 'Same as donor address' : `${donation.pickup.pickupAddress}, ${donation.pickup.pickupCity}, ${donation.pickup.pickupState} ${donation.pickup.pickupZip}`}</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.vehicleDetailsRow}>
                <View style={styles.vehicleDetailCol}>
                  <Text style={styles.tableCell}>Year: {donation.vehicleInfo.year}</Text>
                </View>
                <View style={styles.vehicleDetailCol}>
                  <Text style={styles.tableCell}>Make: {donation.vehicleInfo.make}</Text>
                </View>
                <View style={styles.vehicleDetailCol}>
                  <Text style={styles.tableCell}>Model: {donation.vehicleInfo.model}</Text>
                </View>
                <View style={styles.vehicleDetailCol}>
                  <Text style={styles.tableCell}>Color: {donation.vehicleInfo.color}</Text>
                </View>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Type: {donation.vehicleInfo.vehicleType}</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>VIN: {donation.vehicleInfo.vin}</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Name On Title: {donation.vehicleInfo.nameOnTitle}</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Has Title: {donation.vehicleInfo.titleAvailable === 'yes' ? 'Yes' : 'No'}</Text>
              </View>
            </View>
          </View>
        </View>
        <View style={styles.section}>
          <Text style={styles.bold}>Vehicle Condition</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={styles.fullWidthCol}>
                <View style={styles.checkBoxGroup}>
                  <View style={styles.checkBoxContainer}>
                    <Text style={styles.tableCell}>Running: </Text>
                    <CheckBox checked={donation.vehicleInfo.isRunning === 'yes'} />
                  </View>
                  <View style={styles.checkBoxContainer}>
                    <Text style={styles.tableCell}>Non-Running: </Text>
                    <CheckBox checked={donation.vehicleInfo.isRunning === 'no'} />
                  </View>
                  <View style={styles.checkBoxContainer}>
                    <Text style={styles.tableCell}>Drive 20 Miles: </Text>
                    <CheckBox checked={donation.vehicleInfo.canDrive20Miles === 'yes'} />
                  </View>
                </View>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.fullWidthCol}>
                <Text style={styles.tableCell}>Mechanical Needs: {donation.vehicleInfo.mechanicalCondition}</Text>
              </View>
            </View>
          </View>
        </View>
        <View style={styles.section}>
          <Text style={styles.bold}>Body Condition</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Body Condition: {donation.vehicleInfo.bodyCondition}</Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

DonationRequestWorksheet.propTypes = {
  donation: PropTypes.shape({
    contact: PropTypes.shape({
      title: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      phone: PropTypes.string.isRequired,
      altPhone: PropTypes.string,
      email: PropTypes.string.isRequired,
    }).isRequired,
    vehicleInfo: PropTypes.shape({
      year: PropTypes.string.isRequired,
      make: PropTypes.string.isRequired,
      model: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      vin: PropTypes.string,
      vehicleType: PropTypes.string.isRequired,
      nameOnTitle: PropTypes.string.isRequired,
      titleAvailable: PropTypes.string.isRequired,
      isRunning: PropTypes.string.isRequired,
      canDrive20Miles: PropTypes.string.isRequired,
      bodyCondition: PropTypes.string.isRequired,
      mechanicalCondition: PropTypes.string,
    }).isRequired,
    pickup: PropTypes.shape({
      address: PropTypes.string.isRequired,
      city: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      zip: PropTypes.string.isRequired,
      isCarAtAddress: PropTypes.string.isRequired,
      pickupAddress: PropTypes.string,
      pickupCity: PropTypes.string,
      pickupState: PropTypes.string,
      pickupZip: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default DonationRequestWorksheet;